import { motion } from 'framer-motion';
import {
  Briefcase,
  Building2,
  Calendar,
  Check,
  Download,
  Eye,
  FileText,
  Mail,
  MapPin,
  Phone,
  Search,
  Star,
  Trash2,
  User,
  X,
  Zap
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [applicationsPerPage] = useState(10);
  const { apiCall } = useAuth();

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, filterStatus]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await apiCall('GET', '/api/admin/applications');
      const appArray = response.data?.applications || response.data?.data?.applications || [];
      const processedApps = appArray.map(app => {
        const applicant = app.applicant || app.user || {};
        const userObj = {
          name: applicant.name || `${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim() || applicant.email,
          email: applicant.email,
          phone: applicant.phone
        };

        const jobObj = {
          title: app.job?.title || '',
          company: app.job?.company?.name || app.job?.company || '',
          type: app.job?.employmentType || app.job?.type || ''
        };

        return {
          ...app,
          user: userObj,
          job: jobObj
        };
      });
      setApplications(processedApps);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.job?.company?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(app => app.status === filterStatus);
    }

    setFilteredApplications(filtered);
    setCurrentPage(1);
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      const response = await apiCall('PUT', `/api/admin/applications/${applicationId}/status`, { 
        status: newStatus 
      });
      if (response.success) {
        fetchApplications();
        toast.success(`Application ${newStatus}`);
      }
    } catch (error) {
      console.error('Failed to update application status:', error);
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      try {
        const response = await apiCall('DELETE', `/api/admin/applications/${applicationId}`);
        if (response.success) {
          fetchApplications();
          toast.success('Application deleted successfully');
        }
      } catch (error) {
        console.error('Failed to delete application:', error);
      }
    }
  };

  const openModal = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
  };

  // Pagination
  const indexOfLastApplication = currentPage * applicationsPerPage;
  const indexOfFirstApplication = indexOfLastApplication - applicationsPerPage;
  const currentApplications = filteredApplications.slice(indexOfFirstApplication, indexOfLastApplication);
  const totalPages = Math.ceil(filteredApplications.length / applicationsPerPage);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: '⏳' },
      reviewed: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '👁️' },
      shortlisted: { color: 'bg-green-100 text-green-800 border-green-200', icon: '⭐' },
      interviewed: { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: '💬' },
      hired: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: '✅' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: '❌' },
      withdrawn: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '↩️' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getPriorityColor = (score) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-50';
    if (score >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusCount = (status) => {
    return applications.filter(app => app.status === status).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6"
        >
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Applications Management</h1>
            <p className="text-slate-600 text-lg">Track and manage all job applications across the platform</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="btn-outline flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export Data</span>
            </button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 shadow-soft border border-slate-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Total Applications</p>
                <p className="text-3xl font-bold text-slate-900">{applications.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-soft border border-slate-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Pending Review</p>
                <p className="text-3xl font-bold text-slate-900">{getStatusCount('pending')}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-soft border border-slate-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Shortlisted</p>
                <p className="text-3xl font-bold text-slate-900">{getStatusCount('shortlisted')}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-soft border border-slate-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Hired</p>
                <p className="text-3xl font-bold text-slate-900">{getStatusCount('hired')}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-soft border border-slate-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Rejected</p>
                <p className="text-3xl font-bold text-slate-900">{getStatusCount('rejected')}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                <X className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 shadow-soft border border-slate-200"
        >
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by applicant name, email, job title, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>
            <div className="lg:w-48">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interviewed">Interviewed</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Applications List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-soft border border-slate-200 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Applicant</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Job Position</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Company</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Applied Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Score</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {currentApplications.map((application) => (
                  <tr key={application._id} className="hover:bg-slate-50 transition-colors duration-200">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-lg">
                            {application.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{application.user?.name || 'Unknown'}</p>
                          <div className="flex items-center text-sm text-slate-500 mt-1">
                            <Mail className="w-4 h-4 mr-2" />
                            {application.user?.email || 'No email'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <Briefcase className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="font-medium text-slate-900">{application.job?.title || 'Unknown Position'}</p>
                          <p className="text-sm text-slate-500">{application.job?.type || 'Unknown Type'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-slate-600">
                        <Building2 className="w-4 h-4 mr-2" />
                        {application.job?.company || 'Unknown Company'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-slate-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        {new Date(application.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(application.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(application.matchScore || 0)}`}>
                        <Zap className="w-4 h-4 mr-1" />
                        {application.matchScore || 0}%
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openModal(application)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {application.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(application._id, 'shortlisted')}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors duration-200"
                              title="Shortlist"
                            >
                              <Star className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleStatusChange(application._id, 'rejected')}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {application.status === 'shortlisted' && (
                          <button
                            onClick={() => handleStatusChange(application._id, 'interviewed')}
                            className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                            title="Mark as Interviewed"
                          >
                            <User className="w-4 h-4" />
                          </button>
                        )}
                        
                        {application.status === 'interviewed' && (
                          <button
                            onClick={() => handleStatusChange(application._id, 'hired')}
                            className="p-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors duration-200"
                            title="Hire"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleDeleteApplication(application._id)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          title="Delete Application"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <div className="text-sm text-slate-600">
                Showing {indexOfFirstApplication + 1} to {Math.min(indexOfLastApplication, filteredApplications.length)} of {filteredApplications.length} applications
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-4 py-2 text-sm border rounded-lg transition-colors duration-200 ${
                      currentPage === i + 1
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Application Details Modal */}
        {showModal && selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-3xl font-bold text-slate-900">Application Details</h2>
                  <button
                    onClick={closeModal}
                    className="w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors duration-200"
                  >
                    <X className="w-5 h-5 text-slate-600" />
                  </button>
                </div>

                <div className="space-y-8">
                  {/* Header Info */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-2xl">
                          {selectedApplication.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">{selectedApplication.user?.name}</h3>
                        <p className="text-slate-600 text-lg">{selectedApplication.user?.email}</p>
                        <div className="flex items-center space-x-3 mt-3">
                          {getStatusBadge(selectedApplication.status)}
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedApplication.matchScore || 0)}`}>
                            <Zap className="w-4 h-4 mr-1" />
                            {selectedApplication.matchScore || 0}% Match
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-slate-900">{selectedApplication.job?.title}</p>
                      <p className="text-slate-600">{selectedApplication.job?.company}</p>
                      <p className="text-sm text-slate-500 mt-2">
                        Applied: {new Date(selectedApplication.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h4 className="font-bold text-slate-900 mb-4 flex items-center">
                        <User className="w-5 h-5 mr-2" />
                        Contact Information
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-3 text-slate-400" />
                          <span className="text-slate-700">{selectedApplication.user?.email}</span>
                        </div>
                        {selectedApplication.user?.phone && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-3 text-slate-400" />
                            <span className="text-slate-700">{selectedApplication.user?.phone}</span>
                          </div>
                        )}
                        {selectedApplication.user?.location && (
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-3 text-slate-400" />
                            <span className="text-slate-700">
                              {selectedApplication.user.location.city}, {selectedApplication.user.location.state}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-6">
                      <h4 className="font-bold text-slate-900 mb-4 flex items-center">
                        <Briefcase className="w-5 h-5 mr-2" />
                        Professional Info
                      </h4>
                      <div className="space-y-3">
                        <p><span className="font-medium text-slate-700">Current Role:</span> {selectedApplication.user?.professionalInfo?.currentJobTitle || 'Not specified'}</p>
                        <p><span className="font-medium text-slate-700">Experience:</span> {selectedApplication.user?.professionalInfo?.experienceLevel || 'Not specified'}</p>
                        <p><span className="font-medium text-slate-700">Skills:</span> {selectedApplication.user?.skills?.join(', ') || 'None listed'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Cover Letter */}
                  {selectedApplication.coverLetter && (
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h4 className="font-bold text-slate-900 mb-4 flex items-center">
                        <FileText className="w-5 h-5 mr-2" />
                        Cover Letter
                      </h4>
                      <div className="bg-white p-4 rounded-lg border border-slate-200">
                        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{selectedApplication.coverLetter}</p>
                      </div>
                    </div>
                  )}

                  {/* Application Timeline */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h4 className="font-bold text-slate-900 mb-4">Application Timeline</h4>
                    <div className="space-y-4">
                      {selectedApplication.timeline?.map((event, index) => (
                        <div key={index} className="flex items-center space-x-4 p-4 bg-white rounded-lg border border-slate-200">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{event.action}</p>
                            <p className="text-sm text-slate-600">{new Date(event.date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      )) || (
                        <p className="text-slate-500 text-center py-8">No timeline events recorded</p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
                    {selectedApplication.status === 'pending' && (
                      <>
                        <button
                          onClick={() => {
                            handleStatusChange(selectedApplication._id, 'shortlisted');
                            closeModal();
                          }}
                          className="btn-primary flex items-center"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Shortlist
                        </button>
                        <button
                          onClick={() => {
                            handleStatusChange(selectedApplication._id, 'rejected');
                            closeModal();
                          }}
                          className="btn-danger flex items-center"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Reject
                        </button>
                      </>
                    )}
                    
                    {selectedApplication.status === 'shortlisted' && (
                      <button
                        onClick={() => {
                          handleStatusChange(selectedApplication._id, 'interviewed');
                          closeModal();
                        }}
                        className="btn-primary flex items-center"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Mark as Interviewed
                      </button>
                    )}
                    
                    {selectedApplication.status === 'interviewed' && (
                      <button
                        onClick={() => {
                          handleStatusChange(selectedApplication._id, 'hired');
                          closeModal();
                        }}
                        className="btn-success flex items-center"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Hire Candidate
                      </button>
                    )}
                    
                    <button
                      onClick={closeModal}
                      className="btn-outline"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Applications; 